<template>
  <section
    class="smart-notification-request-band"
    data-testid="smart-notification-request-band"
  >
    <template v-if="sent">
      <h5
        class="band-title"
        data-testid="smart-notification-request-success"
      >
        Thank you!
      </h5>
      <p class="band-subtitle">Our product team has been notified. We may reach out to learn more.</p>
      <a
        href="#"
        class="band-link"
        data-testid="smart-notification-request-again"
        @click.prevent="reset"
      >
        Send another
      </a>
    </template>
    <template v-else>
      <h5 class="band-title">Can't find the alert you need?</h5>
      <p class="band-subtitle">Tell us what you'd want to catch.</p>
      <div class="band-form">
        <AppInput
          v-model="requestText"
          data-test-id="smart-notification-request-input"
          :maxlength="MAX_LENGTH"
          placeholder="e.g. Member logs in from two countries in one day"
          autocomplete="off"
          :disabled="sending"
          @enter="send"
        />
        <AppButton
          type="primary"
          data-testid="smart-notification-request-send"
          :disabled="isSendDisabled"
          @click="send"
        >
          {{ sending ? 'Sending...' : 'Send' }}
        </AppButton>
      </div>
    </template>
  </section>
</template>

<script>
import { SmartNotificationsSettingsResource } from '~/resources';

// Mirrors SmartNotificationRequests::CreateAction::MAX_LENGTH
const MAX_LENGTH = 1000;

/**
 * Freeform "Can't find the alert you need?" request capture. Every accepted request becomes a ProductBoard note
 * (server side) and a `Smart Notification Requested` Segment event; rendering fires
 * `Smart Notification Request Viewed` once. `source` tells both where the band was mounted, so new placements
 * (Members dashboard, rule modal...) only need to pass a new value once the server accepts it.
 */
export default {
  name: 'SmartNotificationRequestBand',
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    source: {
      type: String,
      default: 'smart_notifications_page',
    },
  },
  data: () => ({
    MAX_LENGTH,
    requestText: '',
    sending: false,
    sent: false,
  }),
  computed: {
    trimmedText() {
      return this.requestText.trim().slice(0, MAX_LENGTH);
    },
    isSendDisabled() {
      return this.sending || !this.trimmedText;
    },
    trackingProps() {
      return {
        org_id: String(this.organizationId),
        user_id: String(this.userId),
        source: this.source,
      };
    },
  },
  mounted() {
    window.analytics?.track('Smart Notification Request Viewed', this.trackingProps);
  },
  methods: {
    async send() {
      if (this.isSendDisabled) {
        return;
      }

      this.sending = true;

      try {
        await SmartNotificationsSettingsResource.suggest(
          { organizationId: this.organizationId },
          { request_text: this.trimmedText, source: this.source },
        );

        window.analytics?.track('Smart Notification Requested', {
          ...this.trackingProps,
          request_text: this.trimmedText,
        });

        this.sent = true;
        this.requestText = '';
      } catch (e) {
        this.$appMessage(e?.responseJSON?.errors?.base?.[0] || 'Something went wrong, please try again later.');
      } finally {
        this.sending = false;
      }
    },
    reset() {
      this.sent = false;
    },
  },
};
</script>
